import { EditOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Button, Empty, Spin, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import ModalComponent from '../../../../../components/ModalComponent';
import ReservationRuleGroups, {
  buildAddRulesInput,
  buildGroupsFromAttachments,
  emptyRuleGroups,
  isRuleGroupsInvalid,
} from '../../../../../components/ReservationRuleGroups';
import { ATTACH_TOY_RESERVATION_RULE } from '../../../../configs/modules/graphql/Mutations';
import { TOY_RESERVATION_RULES } from '../../../../configs/modules/graphql/Queries';

const RULE_OPTIONS_LIMIT = 200;

const InventoryReservationRulesModal = ({
  open,
  onClose,
  inventory,
  canEdit = true,
  onSuccess,
}) => {
  const [groups, setGroups] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [rulesByType, setRulesByType] = useState({});
  const [isRulesLoading, setIsRulesLoading] = useState(false);

  const inventoryId = inventory?.id;
  const toyName = inventory?.toy?.name;

  const [fetchSelectableRules] = useLazyQuery(TOY_RESERVATION_RULES, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const grouped = {};
      (response?.toyReservationRules?.data || [])?.forEach((rule) => {
        if (!grouped[rule?.type]) grouped[rule?.type] = [];
        grouped[rule?.type]?.push({ value: rule?.id, label: rule?.name });
      });
      setRulesByType(grouped);
      setIsRulesLoading(false);
    },
    onError: () => {
      setRulesByType({});
      setIsRulesLoading(false);
    },
  });

  const [attachToyReservationRule] = useMutation(ATTACH_TOY_RESERVATION_RULE, {
    onError: () => {},
  });

  const attachments = inventory?.toyReservationRules;

  const syncGroupsFromInventory = () => {
    setGroups(buildGroupsFromAttachments(attachments));
  };

  useEffect(() => {
    if (!open) return;
    setIsEditing(false);
    setErrorMessage('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inventoryId]);

  useEffect(() => {
    if (!open || isEditing) return;
    syncGroupsFromInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inventoryId, attachments, isEditing]);

  const isSubmitDisabled = useMemo(
    () =>
      isRulesLoading || (groups?.length ? isRuleGroupsInvalid(groups) : false),
    [groups, isRulesLoading],
  );

  const startEditing = (seedWithEmptyGroup = false) => {
    setErrorMessage('');
    setIsEditing(true);
    if (seedWithEmptyGroup && !groups?.length) setGroups(emptyRuleGroups());
    if (Object.keys(rulesByType)?.length) return;

    setIsRulesLoading(true);
    fetchSelectableRules({
      variables: {
        filter: { skip: 0, limit: RULE_OPTIONS_LIMIT, isActive: true },
      },
    });
  };

  const cancelEditing = () => {
    setErrorMessage('');
    setIsEditing(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await attachToyReservationRule({
        variables: {
          data: buildAddRulesInput([inventoryId], groups),
        },
      });

      if (!response?.data?.attachToyReservationRule) {
        throw new Error('Failed to update reservation rules');
      }

      message?.success('Reservation rules updated');
      setIsEditing(false);
      onSuccess?.();
    } catch (e) {
      setErrorMessage(
        e?.graphQLErrors?.[0]?.message ||
          e?.networkError?.result?.errors?.[0]?.message ||
          e?.message ||
          'Failed to update reservation rules',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFooter = () => {
    if (isEditing) {
      return [
        <Button key="cancel" onClick={cancelEditing} disabled={isSubmitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Submit
        </Button>,
      ];
    }

    return [
      <Button key="close" onClick={onClose}>
        Close
      </Button>,
      canEdit && groups?.length ? (
        <Button
          key="edit"
          type="primary"
          icon={<EditOutlined />}
          onClick={() => startEditing()}
        >
          Edit
        </Button>
      ) : null,
    ]?.filter(Boolean);
  };

  const isEmptyState = !isEditing && !groups?.length;

  const renderBody = () => {
    if (isEmptyState) {
      return (
        <Empty
          className="reservation-rules-empty"
          image={Empty?.PRESENTED_IMAGE_SIMPLE}
          description={
            <>
              <div className="reservation-rules-empty-title">
                No reservation rules assigned
              </div>
              <div className="reservation-rules-empty-hint">
                Every subscriber can currently see this toy.
              </div>
            </>
          }
        >
          {canEdit && (
            <Button type="primary" onClick={() => startEditing(true)}>
              Assign Rules
            </Button>
          )}
        </Empty>
      );
    }

    if (isEditing && isRulesLoading) {
      return (
        <div className="reservation-rules-loader">
          <Spin />
        </div>
      );
    }

    return (
      <ReservationRuleGroups
        groups={groups}
        rulesByType={rulesByType}
        isRulesLoading={isRulesLoading}
        onGroupsChange={setGroups}
        readOnly={!isEditing}
        emptyDescription="No rule groups yet"
      />
    );
  };

  return (
    <ModalComponent
      open={open}
      onCancel={isSubmitting ? undefined : onClose}
      title={toyName ? `Reservation Rules · ${toyName}` : 'Reservation Rules'}
      footer={renderFooter()}
      width={760}
      className="modal reservation-rules-modal"
      destroyOnClose
      maskClosable={false}
    >
      {errorMessage ? (
        <Alert className="mb-16" type="error" showIcon message={errorMessage} />
      ) : null}

      {!isEmptyState && (
        <div className="font-size-14 mb-16">
          A subscriber sees this toy only when they match <strong>every</strong>{' '}
          group. Inside a group, matching <strong>any one</strong> rule is
          enough.
        </div>
      )}

      <div className="reservation-rules-modal-body">{renderBody()}</div>
    </ModalComponent>
  );
};

export default InventoryReservationRulesModal;
